import {LOCALE_CATEGORIES} from "@/assets/locales/categories";
import {useSessionStore} from "@/domains/-session/stores/useSessionStore"
import {deepmerge} from "@/functions/deepmerge";
import {Lang} from "@/sdk";
import axios from "axios";
import {watch} from "vue";
import {createI18n} from 'vue-i18n'
/* NOTE(Julien)
 * This file have been copied from the MyOmind equivalent file.
 */

/* NOTE(Julien)
 * SUPPORT_LOCALES is used to prevent sending a request to the server if the queried lang is not supported,
 * whenever a new lang is added to the internationalization files,
 * its key must be added to SUPPORT_LOCALES for the files can be fetched from the server.
 */
export const SUPPORT_LOCALES: Lang[] = ['fr', 'en', 'es']
export const SUPPORT_CATEGORIES: LOCALE_CATEGORIES[] = Object.values(LOCALE_CATEGORIES) as LOCALE_CATEGORIES[];

const loadedLocales = {}
loadedLocales['fr'] = {}
loadedLocales['en'] = {}

export const i18n = createI18n({
  legacy: false,
  locale: getUserDefaultLang(), // Use default navigator lang
  fallbackLocale: 'fr',
  messages: loadedLocales
})

const localeCache: { [category in keyof typeof LOCALE_CATEGORIES]?: { [lang in Lang]?: any } } = {}

export function isLocaleCategoryLoaded(lang: Lang, category: LOCALE_CATEGORIES): boolean {
  return category in localeCache && lang in localeCache[category];
}

async function getCategoryMessages(lang: Lang, category: LOCALE_CATEGORIES) {
  if (isLocaleCategoryLoaded(lang, category))
    return;

  if (!SUPPORT_LOCALES.includes(lang))
    throw new Error(`Unsupported language '${lang}' ! Request will not be sent.`);

  if (!SUPPORT_CATEGORIES.includes(category))
    throw new Error(`Unsupported locales category '${category}' ! Request will not be sent.`);

  /* const messagesOLD = await import(`@/assets/locales/${lang}-${category}.json`);
  console.log(messagesOLD) */
  const messages = await (await fetch(`${import.meta.env.VITE_LOCALES_URL}/${import.meta.env.VITE_LOCALES_KEY}/${lang}-${category}.json`)).json()

  if (!(category in localeCache))
    localeCache[category] = {};
  localeCache[category][lang] = true;

  if (!(lang in loadedLocales))
    loadedLocales[lang] = {};
  //loadedLocales[lang] = deepmerge(loadedLocales[lang], messages.default)
  loadedLocales[lang] = deepmerge(loadedLocales[lang], messages)
}


export async function queryLocales(lang: Lang, ...categories: LOCALE_CATEGORIES[]) {
  for (const category of categories) {
    try {
      await getCategoryMessages(lang, category);
    } catch (err) {
      console.error(err);
      console.error(`unable to load ${lang} translations for category ${category} !`);
    }
  }
}

export async function loadLocales(...categories: LOCALE_CATEGORIES[]): Promise<void> {
  const session = useSessionStore()
  await queryLocales(session.lang, ...categories);

  setLocales(session.lang);
}

export async function useWatchLocales(...categories: LOCALE_CATEGORIES[]): Promise<void> {
  const session = useSessionStore()
  await loadLocales(...categories);

  watch(() => session.lang, async () => await loadLocales(...categories));
}

export function setLocales(lang: Lang): void {
  // NOTE(Julien) : this line will force an i18n update in case messages have changed but not locale
  // i18n.global.locale.value = "";

  i18n.global.setLocaleMessage(lang, loadedLocales[lang]);
  i18n.global.locale.value = lang;
  axios.defaults.headers.common['Accept-Language'] = lang;
  document.querySelector('html')?.setAttribute('lang', lang);
}

export async function reloadLocalesForLang(lang: Lang): Promise<void> {
  const localesToLoad: string[] = []
  for (const category in localeCache) {
    if (!localeCache[category][lang]) {
      localesToLoad.push(category)
    }
  }
  await queryLocales(lang, ...localesToLoad as LOCALE_CATEGORIES[]);

  setLocales(lang);
}

export function getUserDefaultLang(): Lang {
  if (/^fr\b/.test(navigator.language))
    return "fr"

  if (/^en\b/.test(navigator.language))
    return "en";

  if (/^es\b/.test(navigator.language))
    return "es";

  // Default lang
  return "en";
}
